import { MemberOnboardingProgress } from '../response-models/member-onboarding-progress.model';
import { OnboardingChecklistItem } from '../response-models/onboarding-checklist-item.model';
import { OnboardingNextStep } from '../response-models/onboarding-next-step.model';
import { OrgOnboardingItem } from '../response-models/org-onboarding-item.model';
import { OwnerOnboardingProgress } from '../response-models/owner-onboarding-progress.model';
import { PersonalOnboardingItem } from '../response-models/personal-onboarding-item.model';
import { isRecord } from './is-record';
import { countOrZero, nullableBoolean, nullableString } from './wire-value-utils';

/** Required items: 4 personal for everyone, plus 2 org items for an Owner / Chief Supervisor. */
const REQUIRED_PERSONAL_ITEMS = 4;
const REQUIRED_ORG_ITEMS = 2;

/** Reads member progress (the hub sends it flat in `data`); `null` when there is none. */
export function toMemberOnboardingProgress(data: unknown): MemberOnboardingProgress | null {
  if (!isRecord(data)) {
    return null;
  }
  return {
    profileCompleted: flag(data['profileCompleted']),
    part107Uploaded: flag(data['part107Uploaded']),
    medicalUploaded: flag(data['medicalUploaded']),
    insuranceUploaded: flag(data['insuranceUploaded']),
    stateLicenseUploaded: flag(data['stateLicenseUploaded']),
    safetyGuidelinesReviewed: flag(data['safetyGuidelinesReviewed']),
    completedAt: nullableString(data['completedAt']) ?? null,
    percentComplete: countOrZero(data['percentComplete']),
    nextSteps: toNextSteps(data['nextSteps']),
    optionalSteps: toNextSteps(data['optionalSteps']),
  };
}

/** Reads owner progress; `null` when there is none. */
export function toOwnerOnboardingProgress(data: unknown): OwnerOnboardingProgress | null {
  if (!isRecord(data)) {
    return null;
  }
  const checklist = Array.isArray(data['checklist']) ? data['checklist'].filter(isRecord).map(toChecklistItem) : [];
  return { checklist, dismissed: flag(data['dismissed']), completedAt: nullableString(data['completedAt']) ?? null };
}

/** The personal setup rows for a member's progress. */
export function toPersonalItems(progress: MemberOnboardingProgress | null): PersonalOnboardingItem[] {
  if (!progress) {
    return [];
  }
  return [
    required('Complete Profile', progress.profileCompleted, 'Name, phone, and address for your pilot account'),
    required('Upload Part 107 Certificate', progress.part107Uploaded, 'Required for FAA-compliant drone operations'),
    optional('Upload FAA Medical Certificate', progress.medicalUploaded),
    optional('Upload Insurance Certificate', progress.insuranceUploaded),
    required('Upload State Applicator License', progress.stateLicenseUploaded, 'Required for agricultural spray operations'),
    required('Review Safety Guidelines', progress.safetyGuidelinesReviewed, 'FAA safety rules and operational best practices'),
  ];
}

/** The visible org setup rows; "Upload Certifications" is shown as "Upload Organization Certifications". */
export function toOrgItems(checklist: OnboardingChecklistItem[]): OrgOnboardingItem[] {
  return checklist
    .filter(item => item.isVisible)
    .map(item => {
      const isUploadCerts = item.itemKey === 'upload_certifications';
      return {
        itemKey: item.itemKey,
        title: isUploadCerts ? 'Upload Organization Certifications' : item.title,
        subtitle: isUploadCerts ? 'Part 137, 44807, Liability Insurance, State Applicator License' : undefined,
        isCompleted: item.isCompleted,
        isRequired: item.isRequired,
        isOptional: !item.isRequired,
        actionRoute: item.actionRoute,
      };
    });
}

/** Combined completion of the required items (org items count only for an Owner / Chief Supervisor). */
export function combinedOnboardingPercentage(
  personal: PersonalOnboardingItem[],
  org: OrgOnboardingItem[],
  includeOrg: boolean
): number {
  if (personal.length === 0) {
    return 0;
  }
  const completed = countCompletedRequired(personal) + (includeOrg ? countCompletedRequired(org) : 0);
  const total = REQUIRED_PERSONAL_ITEMS + (includeOrg ? REQUIRED_ORG_ITEMS : 0);
  return Math.round((completed / total) * 100);
}

/** Whether every required item is complete (org items count only for an Owner / Chief Supervisor). */
export function isOnboardingFullyComplete(
  personal: PersonalOnboardingItem[],
  org: OrgOnboardingItem[],
  includeOrg: boolean
): boolean {
  if (personal.length === 0) {
    return false;
  }
  const allDone = (items: { isRequired: boolean; isCompleted: boolean }[]) =>
    items.filter(item => item.isRequired).every(item => item.isCompleted);
  return allDone(personal) && (!includeOrg || allDone(org));
}

function countCompletedRequired(items: { isRequired: boolean; isCompleted: boolean }[]): number {
  return items.filter(item => item.isRequired && item.isCompleted).length;
}

function required(title: string, isCompleted: boolean, description: string): PersonalOnboardingItem {
  return { title, description, isCompleted, isRequired: true, isOptional: false };
}

function optional(title: string, isCompleted: boolean): PersonalOnboardingItem {
  return { title, isCompleted, isRequired: false, isOptional: true };
}

function toChecklistItem(source: Record<string, unknown>): OnboardingChecklistItem {
  return {
    itemKey: nullableString(source['itemKey']) ?? '',
    title: nullableString(source['title']) ?? '',
    description: nullableString(source['description']) ?? '',
    isCompleted: flag(source['isCompleted']),
    isRequired: flag(source['isRequired']),
    isVisible: flag(source['isVisible']),
    itemOrder: countOrZero(source['itemOrder']),
    completedAt: nullableString(source['completedAt']),
    actionRoute: nullableString(source['actionRoute']) ?? undefined,
  };
}

function toNextSteps(value: unknown): OnboardingNextStep[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map(step => ({
    step: nullableString(step['step']) ?? '',
    required: flag(step['required']),
    url: nullableString(step['url']) ?? '',
  }));
}

function flag(value: unknown): boolean {
  return nullableBoolean(value) ?? false;
}
